import { apiRequest, mockReject } from "./client";
import { getApiBaseUrl } from "../lib/env";

function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  const now = Date.now();
  const timeHex = now.toString(16).padStart(12, "0");
  bytes[0] = parseInt(timeHex.slice(0, 2), 16);
  bytes[1] = parseInt(timeHex.slice(2, 4), 16);
  bytes[2] = parseInt(timeHex.slice(4, 6), 16);
  bytes[3] = parseInt(timeHex.slice(6, 8), 16);
  bytes[4] = parseInt(timeHex.slice(8, 10), 16);
  bytes[5] = parseInt(timeHex.slice(10, 12), 16);

  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

export type CompanyStatus = "Active" | "Inactive";

export type Company = {
  id: string;
  name: string;
  status: CompanyStatus;
  updatedAt: string; // ISO-ish string
};

export type CreateCompanyRequest = {
  companyLegalName: string;
  backOfficeId: string;
  taxId: string;
  legalAddress: string;
  sourceBankCode: string;
  sourceBankAccount: string;
  perBatchLimitIdr: string;
  perTransactionFeeIdr: string;
  picFullname: string;
  picPhonenumber: string;
  picUsername: string;
  reasonDescription?: string;
};

export type UpdateCompanyRequest = Partial<
  Omit<CreateCompanyRequest, "companyLegalName"> & {
    companyLegalName: string;
  }
>;

export type UpdateCompanyStatusRequest = {
  status: CompanyStatus;
};

const MOCK_COMPANIES: Company[] = [
  {
    id: "0195a9b4-9b5a-7f30-9e0b-0a4ad4f2f0e1",
    name: "KamuKirim - Payroll Core",
    status: "Active",
    updatedAt: "2025-02-10",
  },
  {
    id: "0195a9b4-9b5a-7f30-9e0b-0a4ad4f2f0e2",
    name: "Immer External Audit",
    status: "Active",
    updatedAt: "2025-02-07",
  },
  {
    id: "0195a9b4-9b5a-7f30-9e0b-0a4ad4f2f0e3",
    name: "Pelita Nusantara Holdings",
    status: "Inactive",
    updatedAt: "2025-02-02",
  },
];

const useMockCompanies = !getApiBaseUrl();

/**
 * Return a mock list of companies. When a backend is configured, this will call /companies.
 */
export async function listCompanies(): Promise<Company[]> {
  const data = await apiRequest<{ items: Company[] }>({
    path: "/companies",
    method: "GET",
    useMock: useMockCompanies,
    mockResponse: { items: [...MOCK_COMPANIES] },
    delayMs: 350,
  });
  return Array.isArray((data as any)?.items) ? (data as any).items : (data as any);
}

/**
 * Fetch a single company by id (mock-first).
 */
export async function getCompany(id: string): Promise<Company> {
  const found = MOCK_COMPANIES.find((c) => c.id === id);
  if (!found) {
    return mockReject(404, "Company not found", 200);
  }
  return apiRequest<Company>({
    path: `/companies/${id}`,
    method: "GET",
    useMock: useMockCompanies,
    mockResponse: { ...found },
    delayMs: 250,
  });
}

/**
 * Create a company (mock). Generates an id and returns the new record.
 */
export async function createCompany(
  body: CreateCompanyRequest,
): Promise<Company> {
  const trimmedName = body.companyLegalName.trim();
  if (!trimmedName) {
    return mockReject(400, "Company name is required", 0);
  }

  const now = new Date();
  const newCompany: Company = {
    id: uuidv7(),
    name: trimmedName,
    status: "Active",
    updatedAt: now.toISOString().slice(0, 10),
  };

  // Update in-memory mock list to make subsequent mock calls reflect the addition.
  MOCK_COMPANIES.unshift(newCompany);

  return apiRequest<Company, CreateCompanyRequest>({
    path: "/companies",
    method: "POST",
    body,
    useMock: useMockCompanies,
    mockResponse: newCompany,
    delayMs: 350,
  });
}

export async function updateCompany(
  id: string,
  body: UpdateCompanyRequest,
): Promise<Company> {
  return apiRequest<Company, UpdateCompanyRequest>({
    path: `/companies/${id}`,
    method: "PATCH",
    body,
  });
}

export async function updateCompanyStatus(
  id: string,
  status: CompanyStatus,
): Promise<Company> {
  return apiRequest<Company, UpdateCompanyStatusRequest>({
    path: `/companies/${id}/status`,
    method: "PATCH",
    body: { status },
  });
}

export async function bulkUpdateCompanyStatus(
  ids: string[],
  status: CompanyStatus,
): Promise<{ updated: number }> {
  return apiRequest<{ updated: number }, { ids: string[]; status: CompanyStatus }>({
    path: "/companies/bulk/status",
    method: "POST",
    body: { ids, status },
  });
}

export async function exportCompanies(
  ids: string[],
): Promise<{ url: string }> {
  return apiRequest<{ url: string }, { ids: string[] }>({
    path: "/companies/export",
    method: "POST",
    body: { ids },
  });
}
